import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  #onVariantChange = null;

  connectedCallback() {
    super.connectedCallback?.();

    // Bind once so we can remove it later
    this.#onVariantChange = (e) => {
      const t = e.target;
      if (!(t instanceof HTMLInputElement)) return;
      if (!t.matches('[data-variant-variant-input]')) return;

      // Optional: only respond if this quantity selector is on the same product section
      // (prevents issues if you have multiple products on the page)
      const sameSection =
        t.closest('[data-section-id]')?.dataset?.sectionId ===
        this.closest('[data-section-id]')?.dataset?.sectionId;

      if (!sameSection) return;

      this.#syncBuyNowHref();
    };

    document.addEventListener('change', this.#onVariantChange);

    // Optional: ensure link is correct on first load
    this.#syncBuyNowHref();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    if (this.#onVariantChange) {
      document.removeEventListener('change', this.#onVariantChange);
    }
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepUp();
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepDown();
    this.#onQuantityChange();
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();

    const newValue = parseInt(quantityInput.value, 10);

    // keep buy now link synced
    this.#syncBuyNowHref();

    quantityInput.dispatchEvent(
      new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine))
    );
  }

  /**
   * Syncs the Buy Now button URL to /cart/{variantId}:{qty} while preserving query params.
   */
  #syncBuyNowHref() {
  
    // Current variant id (from your variant picker)

    // Current qty
    const qtyInput =
      this.refs?.quantityInput ||
      this.querySelector?.('input[name="quantity"]') ||
      document.querySelector('input[name="quantity"]');





    const qty = Math.max(1, parseInt(qtyInput?.value ?? '1', 10) || 1);

              const checkedVariant =
      // if the variant picker is inside this component, prefer that
      this.querySelector?.('[data-variant-variant-input]:checked') ||
      // otherwise fall back to the page
      document.querySelector('[data-variant-variant-input]:checked');

    const variantId = checkedVariant?.dataset?.variantId || checkedVariant?.value;
    if (variantId) {



    // Optional: scope to this section if you have multiple product sections
    const root = this.closest('[data-section-id]') || document;

    // Find the buy-container for THIS variant (supports both patterns)
    const buyContainer = document.querySelector(`div[data-buy-variant-id="${variantId}"]`);
  
    if (!buyContainer) return;

  
    // Link inside that container
    const buyNowLink = buyContainer.querySelector('a[data-buy-now-button="true"]');
    if (!buyNowLink) return;
    
  

    // Preserve whatever query params are currently on the link (ex: channel=buy_button)
    const oldUrl = new URL(buyNowLink.href);
    const newUrl = new URL(oldUrl.toString());

    newUrl.pathname = `/cart/${variantId}:${qty}`;
    // keep ?channel=buy_button, etc.
    newUrl.search = oldUrl.search;

    buyNowLink.href = newUrl.toString();

      // ---------- 4) Update abar link ----------
    const abarLink =
      this.querySelector?.('.extra_abar a') || document.querySelector('.extra_abar a');

    if (abarLink) {
      
        abarLink.setAttribute('href', newUrl.toString());
      
    }

      // 2) Update "Add to cart" inline price text (.show_price_btn)
      const checkoutButtons = document.querySelectorAll(
        'a.checkout-button'
      );

        checkoutButtons.forEach((buyButton) => {
          buyButton.href = newUrl.toString();
      });

}else {
   var variantInput =
          this.querySelector?.('input[ref="variantId"]') ||
      document.querySelector('input[ref="variantId"]');
if(!variantInput) return;
const variantId = variantInput.value;

  var  newUrl = `/cart/${variantId}:${qty}?channel=buy_button`;

      // ---------- 4) Update abar link ----------
    const abarLink =
      this.querySelector?.('.extra_abar a') || document.querySelector('.extra_abar a');

    if (abarLink) {
      
  abarLink.setAttribute('href', newUrl.toString());
      
    }

      // 2) Update "Add to cart" inline price text (.show_price_btn)
      const checkoutButtons = document.querySelectorAll(
        'a.checkout-button'
      );

        checkoutButtons.forEach((buyButton) => {
         buyButton.href = newUrl.toString();
      });


}

  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules() {
    const { quantityInput } = this.refs;
    const { min, max, value: newValue } = quantityInput;

    if (min && Number(newValue) < Number(min)) quantityInput.value = min;
    if (max && Number(newValue) > Number(max)) quantityInput.value = max;
  }

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}